import assetUtils, { Asset } from 'app-lib/assetUtils'
import numberUtils, { autoFormat, formatPercent } from 'app-lib/numberUtils'
import { ILendItem } from 'bzx-common/src/stores/LendStore/LendStore'
import { observer } from 'mobx-react'
import { IChainConfig } from 'bzx-common/src/config/chains/chainCommon'
import React from 'react'
import ExternalLink from 'shared-components/ExternalLink'
import TokenLoader from 'shared-components/TokenLoader'
import { Button, Icon, InfoTip, Tooltip } from 'ui-framework'
import { BigNumber } from '@0x/utils'
import LendVM from '../LendVM'

/**
 * Returns a twitter intent link for a lending position profit
 */
function getTweetLink(asset: Asset, rate: BigNumber, profit: BigNumber, chain: string) {
  return `https://twitter.com/intent/tweet?original_referer=${encodeURIComponent(
    'https://ooki.com'
  )}&ref_src=twsrc%5Etfw%7Ctwcamp%5Ebuttonembed%7Ctwterm%5Emention%7Ctwgr%5EOokiTrade&screen_name=OokiTrade&text=${encodeURIComponent(
    `\n\nI'm lending $${asset} at ${formatPercent(rate)}% interest rate and made ${autoFormat(
      profit
    )} $${asset} so far on ${chain}.
    \nEasy passive income at https://ooki.com/lend. 😎💰`
  )}`
}

/**
 * Returns a twitter intent link to attract borrowers.
 */
function getTweetLinkBorrow(asset: Asset, rate: BigNumber, chain: IChainConfig) {
  return `https://twitter.com/intent/tweet?original_referer=${encodeURIComponent(
    'https://ooki.com'
  )}&ref_src=twsrc%5Etfw%7Ctwcamp%5Ebuttonembed%7Ctwterm%5Emention%7Ctwgr%5EOokiTrade&screen_name=OokiTrade&text=${encodeURIComponent(
    `\n\nI'm lending $${asset} and it's up for grab, for cheap :)\nBorrow $${asset} at ${formatPercent(
      rate
    )}% on ${chain.twitterHandle}.\n\nAlmost free money at https://ooki.com/borrow! 😎💰`
  )}`
}

/**
 * Displays a user lending position for a particular asset with corresponding profits.
 */
function UserYieldsItem({ vm, lendItem }: { vm: LendVM; lendItem: ILendItem }) {
  const { chain } = vm.rootStore
  const { userYield } = lendItem
  if (!chain || !userYield) {
    return null
  }
  const { chainId } = chain.chainDetails
  const assetInfo = assetUtils.getInfo(chainId, lendItem.asset)
  const { userYearlyProfit, asset, interestRate } = lendItem
  const hasPendingTx = vm.lendStore.pendingAssetTx.some((txAsset) => txAsset === asset)

  const ratePrefix = interestRate.gt(0) && interestRate.lt(0.001) ? '~' : ''
  const rateTip =
    interestRate.lt(5) && interestRate.gt(0) ? (
      <InfoTip id={`lend-apr-tooltip${asset}`} top={-2} delayShow={150}>
        <div className="txt-explanation padded-1 txt-left">
          <h4 className="h4">Ooki lending rates</h4>
          <p>Earn passive income with very high rates, 20%, 50% and much more!</p>
          <p>
            <i>When the pool reaches 80% of utilization</i>, meaning when <b>many</b> people trade
            and borrow on Ooki, you benefit from especially high rates.
          </p>
          <p>Spread the word, get people to use Ooki and start earning from great lending rates.</p>
          <ExternalLink
            className="btn btn--blue btn--narrow block txt-center"
            href={getTweetLinkBorrow(lendItem.asset, interestRate, chain)}>
            Tell people to borrow <Icon name="twitter" size={24} title="Share on Twitter" top={1} />
          </ExternalLink>
        </div>
      </InfoTip>
    ) : null

  // If we could not get the itoken price for some reason, we still display balances but in iToken
  const iTokenSymbol = 'i' + lendItem.asset
  const hasProfit = !userYield.iTokenPrice.isNaN()
  const balanceShown = hasProfit ? userYield.equity : userYield.iTokenBalance
  const balanceShownAsset = hasProfit ? lendItem.asset : iTokenSymbol
  const balanceDescription = hasProfit ? (
    <div className="txt-explanation">
      <p>
        The {autoFormat(userYield.equity)} {lendItem.asset} include your profits.
      </p>
      You have currently{' '}
      <span title={userYield.iTokenBalance.toFixed()}>{autoFormat(userYield.iTokenBalance)}</span>{' '}
      {iTokenSymbol} in your wallet. Each {iTokenSymbol} is worth{' '}
      <span title={userYield.iTokenPrice.toFixed()}>{userYield.iTokenPrice.dp(5).toFixed()}</span>{' '}
      {lendItem.asset}.
    </div>
  ) : (
    <div className="txt-explanation">
      <p>
        You have{' '}
        <span title={userYield.iTokenBalance.toFixed()}>{autoFormat(userYield.iTokenBalance)}</span>{' '}
        {iTokenSymbol} in your wallet.
      </p>
      <p>
        Your profit can not be calculated temporarily because the current {lendItem.asset} iToken
        price is unavailable.
      </p>
      No worries, you are still earning interests, and can still lend or unlend.
    </div>
  )

  return (
    <div className="panel--simple padded-2">
      <div className="title-section flex-row-center">
        <TokenLoader size={38} isLoading={hasPendingTx}>
          <Icon name={assetInfo.spriteLogo} size={38} />
        </TokenLoader>

        <div className="txt-title txt-default margin-left-05">{assetInfo.displayName}</div>
        <ExternalLink
          className="link--no-line flex-row-end"
          href={getTweetLink(asset, interestRate, userYield.profit, chain.chainDetails.shortName)}>
          Share <Icon name="twitter" size={24} title="Share on Twitter" />
        </ExternalLink>
      </div>

      <div className="flex-between margin-top-1">
        <div className="flex-col">
          <div className="txt-subtitle">Avail. Liquidity</div>
          <div title={lendItem.liquidity.toFixed(assetInfo.decimals)} className="txt-title">
            {numberUtils.abbreviate(lendItem.liquidity)}{' '}
            <span className="font-normal">{assetInfo.displayName}</span>
          </div>
        </div>
        <div className="flex-col items-end">
          <div className="flex-row-center">
            <div className="txt-subtitle margin-right-1ch">Balance</div>
            <InfoTip id={`yield-balance-tip-${lendItem.asset}`}>
              <h4 className="h4">Yield balance</h4>
              {balanceDescription}
            </InfoTip>
          </div>

          <div title={`${balanceShown.toFixed()} ${balanceShownAsset}`} className="txt-title">
            {autoFormat(balanceShown)} <span className="font-normal">{balanceShownAsset}</span>
          </div>
        </div>
      </div>

      <div className="flex-between margin-top-1">
        <div className="flex-col">
          <div className="txt-subtitle">Interest Rate</div>
          <div className="txt-title">
            <span title={`${interestRate.toFixed(assetInfo.decimals)}%`}>
              {ratePrefix}
              {formatPercent(interestRate)}
              <span className="txt-alternate">%</span>
            </span>{' '}
            {rateTip}
          </div>
        </div>
        <div className="flex-col items-end">
          <div className="txt-subtitle flex-center">
            <span className="margin-right-1ch">Profit</span>
            <Icon
              name="GreenFaceProfit"
              width="20px"
              height="20px"
              className="tooltip__icon"
              data-tip
              data-for={`profit-tooltip-${lendItem.asset}`}
            />
            <Tooltip
              id={`profit-tooltip-${lendItem.asset}`}
              className="tooltip__info"
              place="top"
              effect="solid"
              border
              borderColor="var(--color-line-separator)">
              <div>
                <div className="txt-muted txt-subtitle padding-bottom-1">
                  Est. Earnings at current APR
                </div>
                <div className="flex-row flex-between">
                  <div className="txt-default txt-alternate">Weekly</div>
                  <div className="txt-title txt-positive text-positive-lend">
                    {autoFormat(userYearlyProfit.div(52))} {assetInfo.displayName}
                  </div>
                </div>
                <div className="flex-row flex-between">
                  <div className="txt-default txt-alternate">Monthly</div>
                  <div className="txt-positive txt-title">
                    {autoFormat(userYearlyProfit.div(12))} {assetInfo.displayName}
                  </div>
                </div>
                <div className="flex-row flex-between">
                  <div className="txt-default txt-alternate">Yearly</div>
                  <div className="txt-positive txt-title">
                    {autoFormat(userYearlyProfit)} {assetInfo.displayName}
                  </div>
                </div>
              </div>
            </Tooltip>
          </div>
          <div title={userYield.profit.toFixed()}>
            <b className="txt-positive">{autoFormat(userYield.profit)}</b> {assetInfo.displayName}
          </div>
        </div>
      </div>
      <hr className="content-separator margin-v-15 width-100p" />
      <div className="flex-between">
        <Button
          variant="blue-inverse"
          onClick={vm.showLendForm}
          onClickEmit="value"
          disabled={hasPendingTx}
          value={lendItem.asset}>
          Lend More
        </Button>
        <Button
          variant="blue-inverse"
          onClick={vm.showUnlendForm}
          onClickEmit="value"
          disabled={hasPendingTx}
          value={lendItem.asset}>
          UnLend
        </Button>
      </div>
    </div>
  )
}

export default observer(UserYieldsItem)
